import React from "react";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Filter chips for Department.
 * value === "all" | one of the live DB department names
 *
 * For staff users with assigned departments, only their assigned chips are
 * shown (the backend hard-filters anyway, but we hide the rest to avoid
 * confusion).
 */
const DepartmentChips = ({ value, onChange, testidPrefix = "dept" }) => {
  const { departments, colorFor } = useDepartments();
  const { user, isManager } = useAuth();
  const userDepts = Array.isArray(user?.departments) ? user.departments : [];
  const visible =
    !isManager && userDepts.length > 0
      ? departments.filter((d) => userDepts.includes(d.name))
      : departments;
  const opts = [
    { key: "all", label: "All depts" },
    ...visible.map((d) => ({ key: d.name, label: d.name })),
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-chips`}>
      {opts.map((o) => {
        const active = value === o.key;
        const accent = colorFor(o.key);
        const isAll = o.key === "all";
        let style;
        if (active && !isAll) {
          // Active department chip — fully saturated colour fill
          style = { backgroundColor: accent, color: "#fff", borderColor: accent };
        } else if (!active && !isAll) {
          // Inactive department chip — subtle tint so the colour is still recognisable
          style = {
            backgroundColor: `${accent}1F`, // ~12% alpha
            color: accent,
            borderColor: accent,
          };
        }
        return (
          <button
            key={o.key}
            data-testid={`${testidPrefix}-${o.key}`}
            onClick={() => onChange(o.key)}
            className={`nb-btn px-3 py-1.5 text-sm transition-all ${
              active ? "nb-btn-dark" : "bg-white"
            }`}
            style={style}
            type="button"
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

export const DepartmentBadge = ({ department, className = "" }) => {
  const { colorFor } = useDepartments();
  if (!department) return null;
  const accent = colorFor(department);
  return (
    <span
      className={`text-[10px] font-display font-bold tracking-wide px-2 py-0.5 rounded-full ${className}`}
      style={{ backgroundColor: `${accent}1A`, color: accent, border: `1px solid ${accent}55` }}
      data-testid={`dept-badge-${department}`}
    >
      {department}
    </span>
  );
};

/**
 * Renders 1..N department badges for a multi-dept stock item. Accepts an
 * array; falls back to the singular `department` prop if `departments` is
 * missing (back-compat with older data).
 */
export const DepartmentBadges = ({ item, className = "" }) => {
  const list =
    Array.isArray(item?.departments) && item.departments.length
      ? item.departments
      : item?.department
        ? [item.department]
        : [];
  if (list.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {list.map((d) => (
        <DepartmentBadge key={d} department={d} className={className} />
      ))}
    </span>
  );
};

export default DepartmentChips;
